import React, { useEffect, useMemo, useState } from 'react'
import api from '../../services/api'
import { Button, Input, Textarea } from '../../components/ui'
import { translateApiError } from '../../utils/errorMessages'
import './Admin.css'

const EMPTY_FORM = {
  id: null,
  question_type: '',
  answer_rules: '',
  example_question: '',
  example_answer: '',
  sort_order: 0,
  is_active: true,
}

function BotResponseRules() {
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [form, setForm] = useState(EMPTY_FORM)

  const isEditMode = useMemo(() => Boolean(form.id), [form.id])

  useEffect(() => {
    loadRules()
  }, [])

  const loadRules = async () => {
    try {
      setLoading(true)
      setMessage({ type: '', text: '' })
      const response = await api.get('/admin/bot-response-rules?include_inactive=true')
      setRules(response?.data?.rules || [])
    } catch (error) {
      setMessage({ type: 'error', text: translateApiError(error.response?.data?.error || error.message) })
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setForm(EMPTY_FORM)
  }

  const onSubmit = async (event) => {
    event.preventDefault()
    if (!String(form.question_type || '').trim()) {
      setMessage({ type: 'error', text: 'Укажите тип запрашиваемых данных' })
      return
    }
    if (!String(form.answer_rules || '').trim()) {
      setMessage({ type: 'error', text: 'Укажите правила ответа' })
      return
    }

    const payload = {
      question_type: String(form.question_type || '').trim(),
      answer_rules: String(form.answer_rules || '').trim(),
      example_question: String(form.example_question || '').trim() || null,
      example_answer: String(form.example_answer || '').trim() || null,
      sort_order: Number.isFinite(Number(form.sort_order)) ? Number(form.sort_order) : 0,
      is_active: Boolean(form.is_active),
    }

    try {
      setSaving(true)
      setMessage({ type: '', text: '' })
      if (isEditMode) {
        await api.put(`/admin/bot-response-rules/${form.id}`, payload)
        setMessage({ type: 'success', text: 'Правило обновлено' })
      } else {
        await api.post('/admin/bot-response-rules', payload)
        setMessage({ type: 'success', text: 'Правило добавлено' })
      }
      resetForm()
      await loadRules()
    } catch (error) {
      setMessage({ type: 'error', text: translateApiError(error.response?.data?.error || error.message) })
    } finally {
      setSaving(false)
    }
  }

  const startEdit = (rule) => {
    setForm({
      id: rule.id,
      question_type: rule.question_type || '',
      answer_rules: rule.answer_rules || '',
      example_question: rule.example_question || '',
      example_answer: rule.example_answer || '',
      sort_order: rule.sort_order ?? 0,
      is_active: Boolean(rule.is_active),
    })
    setMessage({ type: '', text: '' })
  }

  const archiveRule = async (rule) => {
    if (!window.confirm(`Деактивировать правило "${rule.question_type}"?`)) return
    try {
      await api.delete(`/admin/bot-response-rules/${rule.id}`)
      setMessage({ type: 'success', text: 'Правило деактивировано' })
      if (form.id === rule.id) resetForm()
      await loadRules()
    } catch (error) {
      setMessage({ type: 'error', text: translateApiError(error.response?.data?.error || error.message) })
    }
  }

  return (
    <div className="admin-page">
      <p className="admin-subtitle" style={{ marginBottom: 'var(--spacing-lg)' }}>
        Категории вопросов и правила ответов для бота
      </p>

      <div className="admin-settings-panel admin-settings-panel--wide">
        <h2>{isEditMode ? 'Редактирование правила' : 'Новое правило'}</h2>
        <form onSubmit={onSubmit} className="admin-form admin-form--borderless" style={{ display: 'block' }}>
          <div className="form-group">
            <label htmlFor="bot-rule-question-type">Тип запрашиваемых данных</label>
            <Input
              id="bot-rule-question-type"
              value={form.question_type}
              onChange={(e) => setForm((prev) => ({ ...prev, question_type: e.target.value }))}
              placeholder="Например: Статус договора"
            />
          </div>

          <div className="form-group" style={{ marginTop: 'var(--spacing-md)' }}>
            <label htmlFor="bot-rule-answer-rules">Правила ответа</label>
            <Textarea
              id="bot-rule-answer-rules"
              rows={4}
              value={form.answer_rules}
              onChange={(e) => setForm((prev) => ({ ...prev, answer_rules: e.target.value }))}
              placeholder="Как бот должен отвечать на вопросы этой категории"
            />
          </div>

          <div className="form-group" style={{ marginTop: 'var(--spacing-md)' }}>
            <label htmlFor="bot-rule-example-question">Пример вопроса</label>
            <Textarea
              id="bot-rule-example-question"
              rows={2}
              value={form.example_question}
              onChange={(e) => setForm((prev) => ({ ...prev, example_question: e.target.value }))}
            />
          </div>

          <div className="form-group" style={{ marginTop: 'var(--spacing-md)' }}>
            <label htmlFor="bot-rule-example-answer">Пример ответа</label>
            <Textarea
              id="bot-rule-example-answer"
              rows={3}
              value={form.example_answer}
              onChange={(e) => setForm((prev) => ({ ...prev, example_answer: e.target.value }))}
            />
          </div>

          <div className="form-group" style={{ marginTop: 'var(--spacing-md)' }}>
            <label htmlFor="bot-rule-sort-order">Порядок</label>
            <Input
              id="bot-rule-sort-order"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm((prev) => ({ ...prev, sort_order: e.target.value }))}
            />
          </div>

          <div className="form-group" style={{ marginTop: 'var(--spacing-md)' }}>
            <label className="checkbox-inline" htmlFor="bot-rule-is-active">
              <input
                id="bot-rule-is-active"
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
              />
              <span>Активно</span>
            </label>
          </div>

          {message.text && (
            <p style={{ color: message.type === 'error' ? 'var(--color-error)' : 'var(--color-success)', marginTop: 'var(--spacing-sm)' }}>
              {message.text}
            </p>
          )}

          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-md)' }}>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Сохранение...' : (isEditMode ? 'Сохранить' : 'Добавить')}
            </Button>
            {isEditMode && (
              <Button type="button" variant="secondary" onClick={resetForm}>
                Отмена
              </Button>
            )}
          </div>
        </form>
      </div>

      <div className="admin-settings-panel admin-settings-panel--wide">
        <h2>Список правил</h2>
        {loading ? (
          <p>Загрузка...</p>
        ) : (
          <div className="admin-table-frame admin-table-frame--plain-first">
            <div className="table-responsive">
              <table className="ui-table data-table">
                <thead>
                  <tr>
                    <th>Тип данных</th>
                    <th>Правила ответа</th>
                    <th>Пример вопроса</th>
                    <th>Пример ответа</th>
                    <th>Порядок</th>
                    <th>Статус</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.length === 0 ? (
                    <tr>
                      <td colSpan={7}>Нет правил</td>
                    </tr>
                  ) : rules.map((rule) => (
                    <tr key={rule.id}>
                      <td>{rule.question_type}</td>
                      <td>{rule.answer_rules}</td>
                      <td>{rule.example_question || '—'}</td>
                      <td>{rule.example_answer || '—'}</td>
                      <td>{rule.sort_order ?? 0}</td>
                      <td>{rule.is_active ? 'Активно' : 'Отключено'}</td>
                      <td style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                        <Button type="button" variant="secondary" onClick={() => startEdit(rule)}>Изменить</Button>
                        {rule.is_active && (
                          <Button type="button" variant="ghost" onClick={() => archiveRule(rule)}>Отключить</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default BotResponseRules
